import { PracticeVideoPart, PracticeVideos, YouTubeVideoId } from './model.ts';

// YouTube links for practice videos (GitHub issue #4041): church admins paste any form of a YouTube link
// and the org stores only the video id

const videoIdPattern = /^[A-Za-z0-9_-]{11}$/;

// whether the text has the form of a YouTube video id: 11 letters, digits, '-' or '_'
export function isYouTubeVideoId(text: string): text is YouTubeVideoId {
  return videoIdPattern.test(text);
}

// the path prefixes that carry the video id as the next segment, by host (www. and m. stripped)
const idPathPrefixesByHost: Record<string, string[]> = {
  'youtube.com': ['shorts', 'embed', 'live'],
  'youtube-nocookie.com': ['embed'],
};

// the four voice parts, in the order the player and the edit-song pop-up list them
export const practiceVideoParts: PracticeVideoPart[] = ['soprano', 'alto', 'tenor', 'bass'];

// every practice video an org can store for a song: the four parts, then the whole-choir video ('all')
export const allPracticeVideoParts: PracticeVideoPart[] = [...practiceVideoParts, 'all'];

// each video's name in the edit-song pop-up and on the video's badge in the player
export const practiceVideoPartLabels: Record<PracticeVideoPart, string> = {
  soprano: 'Soprano',
  alto: 'Alto',
  tenor: 'Tenor',
  bass: 'Bass',
  all: 'All parts',
};

/**
 * The video id in a pasted YouTube link (`youtu.be/<id>`, `youtube.com/watch?v=<id>`, `/shorts/<id>`,
 * `/embed/<id>`, `/live/<id>`, `youtube-nocookie.com/embed/<id>`, with or without the scheme, `www.` or `m.`);
 * undefined for anything else. A bare id is not a link: an 11-letter word typed into the field (`placeholder`)
 * must not be saved as a video.
 */
export function parseYouTubeVideoId(input: string): YouTubeVideoId | undefined {
  const text = input.trim();
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return undefined;
  }
  const host = url.hostname.toLowerCase().replace(/^(www|m)\./, '');
  const [first, second] = url.pathname.split('/').filter(Boolean);
  const candidate =
    host === 'youtu.be'
      ? first
      : host === 'youtube.com' && first === 'watch'
        ? url.searchParams.get('v')
        : idPathPrefixesByHost[host]?.includes(first)
          ? second
          : undefined;
  return candidate && isYouTubeVideoId(candidate) ? candidate : undefined;
}

// the link the field shows after parsing and "Open in YouTube" opens
export function youTubeWatchUrl(id: YouTubeVideoId): string {
  return `https://youtu.be/${id}`;
}

// YouTube's 320x180 thumbnail of the video
export function youTubeThumbnailUrl(id: YouTubeVideoId): string {
  return `https://i.ytimg.com/vi/${id}/mqdefault.jpg`;
}

// how many voice parts have a video, and whether there is a whole-choir video
export function practiceVideoCount(videos: PracticeVideos | undefined): { parts: number; hasWholeSong: boolean } {
  return {
    parts: practiceVideoParts.filter((part) => videos?.[part]).length,
    hasWholeSong: Boolean(videos?.all),
  };
}
